(() => {
  "use strict";

  window.rdvdeco = window.rdvdeco || {};

  window.rdvdeco.ProductGrid = class extends HTMLElement {
    connectedCallback() {
      this.productGridSelector = ".products-list";
      this.loadMoreButtonSelector = ".infinite-scroll";
      this.loadMoreButtonTextSelector = ".btn-text";
      this.loadMoreButtonProductCountSelector = ".product-count span";
      this.productCardSelector = ".product-list-card";
      this.promotionCardSelector = ".push-wrapper";
      this.cache = new window.rdvdeco.CacheManager();
      this.loading = false;
      this._prefetch = null;
      this._observer = null;

      this.seeMoreButton.addEventListener("click", this.onSeeMoreClick);
      this.seeMoreButton.addEventListener("mouseenter", this.onSeeMoreHover, { once: true });
      this.updateProductCountText(this.productGrid);

      if (this.seeMoreButton.dataset.type === "infinite") {
        this._setupInfiniteScroll();
      }

      window.addEventListener("popstate", this.onPopState);
      document.addEventListener("collection:rerendered", this.onCollectionRerendered);
    }

    disconnectedCallback() {
      window.removeEventListener("popstate", this.onPopState);
      document.removeEventListener("collection:rerendered", this.onCollectionRerendered);
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
    }

    get productGrid() {
      return this.cache.get("dom", "productGrid", () => {
        return document.querySelector(this.productGridSelector);
      });
    }

    get seeMoreButton() {
      return this.cache.get("dom", "seeMoreButton", () => {
        return document.querySelector(this.loadMoreButtonSelector);
      });
    }

    get pageNumber() {
      return this.productGrid.dataset.pageNumber;
    }

    get productShownCount() {
      return this.productGrid.dataset.productShownCount;
    }

    get productShownText() {
      return this.cache.get("dom", "shownProductCount", () => {
        return this.querySelector(this.loadMoreButtonProductCountSelector);
      });
    }

    get endpoint() {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.searchParams.set('page', Number(this.pageNumber) + 1);
      return url.toString();
    }

    _setupInfiniteScroll() {
      this._observer = new IntersectionObserver(
        (entries) => { if (entries[0].isIntersecting) this.loadProducts(); },
        { rootMargin: '200px 0px 200px 0px' }
      );
      this._observer.observe(this.seeMoreButton);
    }

    onSeeMoreHover = () => {
      this._prefetch = fetch(this.endpoint);
    };

    onSeeMoreClick = async (event) => {
      event.preventDefault();
      await this.loadProducts();
    };

    loadProducts = async () => {
      if (this.loading) return;

      try {
        this.enableLoading();

        const response = await (this._prefetch || fetch(this.endpoint));
        this._prefetch = null;

        if (!response.ok) throw new Error("Network response was not ok");

        const nextPageMarkup = await response.text();
        const nextPageHTML = new DOMParser().parseFromString(nextPageMarkup, "text/html");
        const nextProductList = nextPageHTML.querySelector(this.productGridSelector);

        if (!nextProductList)
          throw new Error("Could not find product list in the fetched page");

        const fragment = document.createDocumentFragment();
        Array.from(nextProductList.children).forEach(child =>
          fragment.appendChild(child.cloneNode(true))
        );
        this.productGrid.appendChild(fragment);

        const newSliders = this.productGrid.querySelectorAll('card-product-image-slider:not(.swiper-initialized)');
        if (newSliders.length > 0 && typeof sliderCardProduct !== 'undefined') {
          sliderCardProduct(Array.from(newSliders));
        }

        this.updateProgress(nextPageHTML);

        if (typeof SkeepersProductWidget !== 'undefined') {
          SkeepersProductWidget.generate();
        }
      } catch (error) {
        console.error("Error loading more products", error);
      } finally {
        this.disableLoading();
      }
    };

    onCollectionRerendered = () => {
      this._prefetch = null;
      this.cache.clear("dom");

      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }

      const button = this.seeMoreButton;
      if (!button) return;

      button.addEventListener("click", this.onSeeMoreClick);
      button.addEventListener("mouseenter", this.onSeeMoreHover, { once: true });

      if (button.dataset.type === "infinite") {
        this._setupInfiniteScroll();
      }

      this.updateProductCountText(this.productGrid);
    };

    onPopState = () => {
      window.location.reload();
    };

    updateProgress(nextPageHTML) {
      const nextPageNumber = Number(this.pageNumber) + 1;
      this.productGrid.dataset.pageNumber = nextPageNumber;

      const isLastPage =
        nextPageHTML.querySelector(this.productGridSelector).dataset.pageNumber === "last";

      if (isLastPage) {
        this.productGrid.dataset.pageNumber = "last";
        if (this._observer) {
          this._observer.disconnect();
          this._observer = null;
        }
        this.seeMoreButton.remove();
        this.cache.removeIf("dom", (_, key) => key === "seeMoreButton");
      }

      const url = new URL(window.location.href);
      url.searchParams.set('page', nextPageNumber);
      history.pushState({ page: nextPageNumber }, '', url.toString());

      if (!isLastPage && this.seeMoreButton) {
        const nextUrl = new URL(window.location.href);
        nextUrl.searchParams.set('page', nextPageNumber + 1);
        this.seeMoreButton.setAttribute('href', nextUrl.toString());
        this.seeMoreButton.addEventListener("mouseenter", this.onSeeMoreHover, { once: true });
      }

      this.updateProductCountText(nextPageHTML);
    }

    updateProductCountText(pageHtml) {
      const newProductCount = pageHtml.querySelectorAll(this.productCardSelector).length;
      const newPromoCount = pageHtml.querySelectorAll(this.promotionCardSelector).length;
      const prev = Number(this.productGrid.dataset.productShownCount || 0);
      this.productGrid.dataset.productShownCount = prev + newProductCount - newPromoCount;
    }

    enableLoading() {
      this.loading = true;
      this.seeMoreButton.classList.add('pointer-events-none', 'btn--loading');
    }

    disableLoading() {
      this.loading = false;
      this.seeMoreButton.classList.remove('pointer-events-none', 'btn--loading');
    }
  };

  if (!window.customElements.get("rdvdeco-product-grid")) {
    window.customElements.define(
      "rdvdeco-product-grid",
      window.rdvdeco.ProductGrid
    );
  }
})();
